import { useState } from 'react';
import { X, Plus, Trash2, Type, Hash, ChevronsUpDown, Calendar, CheckSquare } from 'lucide-react';
import { useTeamFields, type CustomFieldType } from '@/lib/customFields';
import { cn } from '@/lib/utils';

const TYPES: { id: CustomFieldType; label: string; icon: typeof Type }[] = [
  { id: 'text', label: 'Text', icon: Type },
  { id: 'number', label: 'Number', icon: Hash },
  { id: 'select', label: 'Select', icon: ChevronsUpDown },
  { id: 'date', label: 'Date', icon: Calendar },
  { id: 'checkbox', label: 'Checkbox', icon: CheckSquare },
];

export function FieldBuilder({
  open,
  teamId,
  teamName,
  onClose,
}: {
  open: boolean;
  teamId: string;
  teamName: string;
  onClose: () => void;
}) {
  const { fields, add, remove } = useTeamFields(teamId);
  const [label, setLabel] = useState('');
  const [type, setType] = useState<CustomFieldType>('text');
  const [optionsStr, setOptionsStr] = useState('');
  const [required, setRequired] = useState(false);

  if (!open) return null;

  const submit = () => {
    if (!label.trim()) return;
    add({
      label: label.trim(),
      type,
      required,
      options: type === 'select' ? optionsStr.split(',').map((o) => o.trim()).filter(Boolean) : undefined,
    });
    setLabel('');
    setOptionsStr('');
    setRequired(false);
    setType('text');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-6 pt-[10vh]">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <div className="text-base font-semibold">Custom fields</div>
            <div className="text-xs text-muted-foreground">Extra fields for {teamName} tickets</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-5">
          {/* existing */}
          {fields.length > 0 ? (
            <div className="space-y-1.5">
              {fields.map((f) => (
                <div key={f.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                  <span className="font-medium">{f.label}</span>
                  <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">{f.type}</span>
                  {f.required && <span className="text-[10px] text-temp-ember">required</span>}
                  {f.type === 'select' && f.options && (
                    <span className="truncate text-[11px] text-muted-foreground">{f.options.join(' · ')}</span>
                  )}
                  <button onClick={() => remove(f.id)} className="ml-auto text-muted-foreground hover:text-temp-ember">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-2 text-sm text-muted-foreground">No custom fields yet. Add one below.</p>
          )}

          {/* add new */}
          <div className="mt-4 space-y-3 rounded-xl border border-dashed border-border p-3">
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Field label — e.g. Affected service tier"
              className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-primary"
            />
            <div className="flex flex-wrap gap-1">
              {TYPES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setType(t.id)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition',
                    type === t.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground',
                  )}
                >
                  <t.icon className="h-3.5 w-3.5" /> {t.label}
                </button>
              ))}
            </div>
            {type === 'select' && (
              <input
                value={optionsStr}
                onChange={(e) => setOptionsStr(e.target.value)}
                placeholder="Options, comma-separated — e.g. Tier 1, Tier 2, Tier 3"
                className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-primary"
              />
            )}
            <div className="flex items-center justify-between">
              <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} className="accent-primary" />
                Required
              </label>
              <button
                onClick={submit}
                disabled={!label.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
              >
                <Plus className="h-4 w-4" /> Add field
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
